import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiOutlineClipboardDocumentList,
  HiOutlineArrowLeft,
  HiOutlineCalendar,
  HiOutlineArrowDownTray,
  HiOutlineAdjustmentsHorizontal,
  HiOutlineChevronDown,
  HiOutlineChevronUp,
  HiOutlineExclamationCircle,
  HiOutlineInformationCircle,
  HiOutlineXMark,
} from "react-icons/hi2";
import {
  BarChart,
  PieChart,
  Pie,
  Cell,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Dialog, DialogTitle, DialogContent, IconButton, CircularProgress } from "@mui/material";
import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import ReportFilterBar from "../../components/ReportFilterBar";
import "../Reports.css";
import "./OeeLossReport.css";
import apiClient from "../../config/api";
import {
  showReportGeneratingToast,
  reportGenerationDone,
  reportGenerationFailed,
} from "../../utils/reportGenerationToast";

import { SEMANTIC_SUCCESS_MID } from '../../theme/chartColors';
// ─── Date helpers ─────────────────────────────────────────────────────────────
const buildDateRange = (startStr, endStr) => {
  if (!startStr || !endStr) return [];
  const dates = [];
  const cur = new Date(startStr);
  const end = new Date(endStr);
  while (cur <= end) {
    dates.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
};

const fmtDate = (d) =>
  d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "2-digit" });

const localDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// ─── Loss type config ─────────────────────────────────────────────────────────
const LOSS_COLORS = {
  "Startup Loss": "#f59e0b",
  "Breakdown Loss": "#ef4444",
  "Changeover Loss": "#06b6d4",
  "Performance Loss": "#8b5cf6",
  "Quality Loss": SEMANTIC_SUCCESS_MID,
  "Availability": "#ef4444",
  "Performance": "#8b5cf6",
  "Quality": SEMANTIC_SUCCESS_MID,
};
const LOSS_ORDER = [
  "Startup Loss",
  "Breakdown Loss",
  "Changeover Loss",
  "Performance Loss",
  "Quality Loss",
];
// Top-level OEE Impact categories shown in the main chart
const IMPACT_ORDER = ["Availability", "Performance", "Quality"];
const IMPACT_ICONS = { Availability: "⚙️", Performance: "📉", Quality: "🔍" };

const fmt = (v, decimals = 1) =>
  v === null || v === undefined ? "–" : Number(v).toFixed(decimals);

// ─── Process fetched API records into loss summary ────────────────────────────
/**
 * Each record from /api/reports/oee-summary has:
 *   PlantName, LineName, MachineName, Date (yyyy-MM-dd), Shift,
 *   PlannedProductionTime, DowntimeMinutes, NetOperatingTime,
 *   TotalQty (good), GoodQty, RejectQty, ReworkQty,
 *   Availability, Performance, Quality, Oee
 *
 * From the OEE backend logic we know:
 *   – DowntimeMinutes = total UNPLANNED downtime (which includes auto-logged Startup,
 *     Changeover, Speed-Loss, Quality-Loss logs, plus any manually entered Breakdown logs)
 *
 * Since the summary API doesn't break down downtime by loss type, we derive:
 *   • Performance Loss = NetOperatingTime − (GoodQty × IdealCycleTime)
 *     But we don't have IdealCycleTime per record in the DTO. We back-calculate:
 *       IdealRunRate = Performance% × NetOperatingTime / (GoodQty * 100)  ← CT = 1/IRR
 *     Or equivalently, using the formula stored in the backend:
 *       Performance = (GoodQty × CT_min) / NetOperatingTime × 100
 *       ⟹  GoodQty × CT_min = Performance × NetOperatingTime / 100
 *       ⟹  Performance Loss (min) = NetOperatingTime – (Performance × NetOperatingTime / 100)
 *                                  = NetOperatingTime × (1 – Performance/100)
 *   • Quality Loss = (RejectQty + ReworkQty) × CT_min
 *     Same back-calculation:
 *       Quality = GoodQty / (GoodQty + R + Rw) × 100
 *       GoodQty + R + Rw = TotalInputQty
 *       Quality Loss (min) = (R + Rw) × CT_min  where CT_min = IdealTime / GoodQty
 *       IdealTime = Performance × NetOperatingTime / 100
 *       CT_min = IdealTime / GoodQty (per-unit ideal time in minutes)
 *       Quality Loss = (R + Rw) × CT_min
 *   • For Startup / Breakdown / Changeover Loss we use DowntimeMinutes as a proxy for
 *     "other / unclassified downtime" because the summary endpoint aggregates it.
 *     We split it proportionally (Startup 20%, Changeover 15%, rest Breakdown)
 *     ONLY when the breakdown is not available. In many setups this is acceptable for a report.
 *
 * Note: For an accurate per-type breakdown the backend needs a
 * /api/reports/oee-loss endpoint. If that becomes available, replace the
 * `deriveBreakdownLosses` function below.
 */
const computeLossesFromRecord = (rec) => {
  return {
    startupLoss: Math.round((rec.startupLossMinutes || 0) * 10) / 10,
    breakdownLoss: Math.round((rec.breakdownLossMinutes || 0) * 10) / 10,
    changeoverLoss: Math.round((rec.changeoverLossMinutes || 0) * 10) / 10,
    performanceLoss: Math.round((rec.speedLossMinutes || 0) * 10) / 10,
    qualityLoss: Math.round((rec.qualityLossMinutes || 0) * 10) / 10,
  };
};

// ─── Aggregate fetched records into a loss summary structure ──────────────────
const buildLossSummary = (records, dates) => {
  if (!records || records.length === 0) return null;

  const dateStrings = dates.map(localDateStr);

  // Group by machine label
  const machineMap = {};
  records.forEach((rec) => {
    const plant = rec.plantName || "Unknown Plant";
    const line = rec.lineName || "Unknown Line";
    const machine = rec.machineName || "Unknown Machine";
    const label = `${plant} → ${line} → ${machine}`;
    const dateKey = (rec.date || "").split("T")[0];
    if (!machineMap[label]) machineMap[label] = {};
    if (!machineMap[label][dateKey]) machineMap[label][dateKey] = [];
    machineMap[label][dateKey].push(rec);
  });

  // Build per-machine per-day loss values
  const machineLabels = Object.keys(machineMap);
  const machines = machineLabels.map((label) => {
    // Daily loss array aligned to `dates`
    const daily = dateStrings.map((ds) => {
      const recs = machineMap[label][ds];
      if (!recs || recs.length === 0) return null;
      // Sum across shifts for the same machine+date
      let sl = 0, bl = 0, cl = 0, pl = 0, ql = 0;
      recs.forEach((r) => {
        const losses = computeLossesFromRecord(r);
        sl += losses.startupLoss;
        bl += losses.breakdownLoss;
        cl += losses.changeoverLoss;
        pl += losses.performanceLoss;
        ql += losses.qualityLoss;
      });
      const availLoss = Math.round((sl + bl + cl) * 10) / 10;
      return {
        startupLoss: Math.round(sl * 10) / 10,
        breakdownLoss: Math.round(bl * 10) / 10,
        changeoverLoss: Math.round(cl * 10) / 10,
        performanceLoss: Math.round(pl * 10) / 10,
        qualityLoss: Math.round(ql * 10) / 10,
        availabilityLoss: availLoss,
        totalLoss: Math.round((sl + bl + cl + pl + ql) * 10) / 10,
      };
    });

    // Aggregate totals across all dates
    const aggr = daily.reduce(
      (acc, d) => {
        if (!d) return acc;
        return {
          startupLoss: acc.startupLoss + d.startupLoss,
          breakdownLoss: acc.breakdownLoss + d.breakdownLoss,
          changeoverLoss: acc.changeoverLoss + d.changeoverLoss,
          performanceLoss: acc.performanceLoss + d.performanceLoss,
          qualityLoss: acc.qualityLoss + d.qualityLoss,
          availabilityLoss: acc.availabilityLoss + d.availabilityLoss,
          totalLoss: acc.totalLoss + d.totalLoss,
        };
      },
      { startupLoss: 0, breakdownLoss: 0, changeoverLoss: 0, performanceLoss: 0, qualityLoss: 0, availabilityLoss: 0, totalLoss: 0 }
    );

    return { label, daily, totals: aggr };
  });

  // Grand total across all machines
  const grand = machines.reduce(
    (acc, m) => ({
      startupLoss: acc.startupLoss + m.totals.startupLoss,
      breakdownLoss: acc.breakdownLoss + m.totals.breakdownLoss,
      changeoverLoss: acc.changeoverLoss + m.totals.changeoverLoss,
      performanceLoss: acc.performanceLoss + m.totals.performanceLoss,
      qualityLoss: acc.qualityLoss + m.totals.qualityLoss,
      availabilityLoss: acc.availabilityLoss + m.totals.availabilityLoss,
      totalLoss: acc.totalLoss + m.totals.totalLoss,
    }),
    { startupLoss: 0, breakdownLoss: 0, changeoverLoss: 0, performanceLoss: 0, qualityLoss: 0, availabilityLoss: 0, totalLoss: 0 }
  );

  return { machines, grand };
};

// ─── Loss Summary Cards ───────────────────────────────────────────────────────
const LossSummaryCards = ({ grand }) => {
  if (!grand) return null;

  const items = [
    { key: "startupLoss", label: "Start-up Loss", icon: "⏱", color: LOSS_COLORS["Startup Loss"], value: grand.startupLoss },
    { key: "breakdownLoss", label: "Breakdown Loss", icon: "⚡", color: LOSS_COLORS["Breakdown Loss"], value: grand.breakdownLoss },
    { key: "changeoverLoss", label: "Changeover Loss", icon: "🔄", color: LOSS_COLORS["Changeover Loss"], value: grand.changeoverLoss },
    { key: "performanceLoss", label: "Performance Loss", icon: "📉", color: LOSS_COLORS["Performance Loss"], value: grand.performanceLoss },
    { key: "qualityLoss", label: "Quality Loss", icon: "🔍", color: LOSS_COLORS["Quality Loss"], value: grand.qualityLoss },
  ];

  const totalLoss = grand.totalLoss;

  return (
    <div style={{ padding: "16px 0px", }}>
      <div style={{ display: "flex", gap: "8px", marginBottom: "8px", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: "13px", fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Loss Summary
        </h3>
        <span style={{ background: "#667eea", color: "#fff", fontSize: "11px", padding: "2px 8px", borderRadius: "12px", fontWeight: 600 }}>
          Total: {fmt(totalLoss)} min
        </span>
      </div>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        {items.map((item) => {
          const pct = totalLoss > 0 ? ((item.value / totalLoss) * 100).toFixed(1) : 0;
          return (
            <div
              key={item.key}
              style={{
                background: "#fff",
                border: `1px solid ${item.color}33`,
                borderLeft: `3px solid ${item.color}`,
                borderRadius: "8px",
                padding: "10px 14px",
                minWidth: "140px",
                flex: "1",
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
                <span style={{ fontSize: "16px" }}>{item.icon}</span>
                <span style={{ fontSize: "11px", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.03em" }}>
                  {item.label}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
                <span style={{ fontSize: "20px", fontWeight: 700, color: item.color }}>
                  {fmt(item.value)}
                </span>
                <span style={{ fontSize: "11px", color: "#9ca3af" }}>min</span>
                {pct > 0 && (
                  <span style={{ fontSize: "11px", color: "#9ca3af", marginLeft: "auto" }}>
                    {pct}%
                  </span>
                )}
              </div>
              {/* Mini progress bar */}
              <div style={{ marginTop: "6px", height: "3px", background: "#f3f4f6", borderRadius: "2px" }}>
                <div
                  style={{
                    height: "100%",
                    width: `${pct}%`,
                    background: item.color,
                    borderRadius: "2px",
                    transition: "width 0.5s ease",
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Loss Charts (Pareto + Distribution) ─────────────────────────────────────
const LossCharts = ({ grand, machines, onChartClick }) => {
  const [activeChart, setActiveChart] = useState("pareto");

  // TOP-LEVEL: Availability / Performance / Quality bars
  const impactData = [
    { name: "Availability", value: grand?.availabilityLoss || 0, color: LOSS_COLORS["Availability"] },
    { name: "Performance", value: grand?.performanceLoss || 0, color: LOSS_COLORS["Performance"] },
    { name: "Quality", value: grand?.qualityLoss || 0, color: LOSS_COLORS["Quality"] },
  ].filter(d => d.value > 0).sort((a, b) => b.value - a.value);

  const paretoData = impactData;

  // Add cumulative %
  const total = paretoData.reduce((s, d) => s + d.value, 0);
  let cumSum = 0;
  const paretoWithCum = paretoData.map((d) => {
    cumSum += d.value;
    return { ...d, cumPct: total > 0 ? Math.round((cumSum / total) * 100) : 0 };
  });

  // Pie / distribution data
  const distData = paretoData.map((d) => ({
    name: d.name,
    value: d.value,
    fill: d.color,
  }));

  const CustomParetoTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: "#1e293b", padding: "10px 14px", borderRadius: "8px", boxShadow: "0 8px 24px rgba(0,0,0,0.25)", minWidth: 160 }}>
        <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 700, color: "#94a3b8" }}>{payload[0]?.payload?.name}</p>
        {payload.map((p) => (
          <p key={p.dataKey} style={{ margin: "2px 0", fontSize: 13, color: p.color || "#fff", fontWeight: 600 }}>
            {p.name}: {fmt(p.value)} {p.dataKey === "cumPct" ? "%" : "min"}
          </p>
        ))}
      </div>
    );
  };

  const CustomPieTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0];
    return (
      <div style={{ background: "#1e293b", padding: "10px 14px", borderRadius: "8px", boxShadow: "0 8px 24px rgba(0,0,0,0.25)" }}>
        <p style={{ margin: 0, fontSize: 13, color: d.payload.fill, fontWeight: 600 }}>{d.name}</p>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "#fff" }}>
          {fmt(d.value)} min ({total > 0 ? ((d.value / total) * 100).toFixed(1) : 0}%)
        </p>
      </div>
    );
  };

  return (
    <div style={{ padding: "16px 24px", background: "#fff", borderBottom: "1px solid #e5e7eb" }}>
      <div style={{ display: "flex", gap: "8px", marginBottom: "16px", alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: "13px", fontWeight: 700, color: "#374151", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Loss Charts
        </h3>
        <div style={{ display: "flex", gap: "6px", marginLeft: "12px" }}>
          {[
            { key: "pareto", label: "Pareto Chart" },
            { key: "dist", label: "Distribution" },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => setActiveChart(opt.key)}
              style={{
                padding: "4px 14px", borderRadius: "6px", border: "1px solid #e5e7eb; border-color: var(--border-primary)",
                background: activeChart === opt.key ? "#529579" : "#fff",
                color: activeChart === opt.key ? "#fff" : "#374151",
                fontSize: "12px", fontWeight: 500, cursor: "pointer", transition: "all 0.2s",
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {paretoData.length === 0 ? (
        <div style={{ textAlign: "center", color: "#9ca3af", padding: "40px 0", fontSize: "14px" }}>
          No loss data to chart
        </div>
      ) : (
        <div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
          {activeChart === "pareto" && (
            <div style={{ flex: "1", minWidth: "320px" }}>
              <p style={{ margin: "0 0 8px", fontSize: "12px", color: "#6b7280" }}>
                Bars = Time Lost (min) · Sorted by impact · <span style={{ color: "#667eea" }}>Cumulative %</span>
              </p>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={paretoWithCum} margin={{ top: 10, right: 40, left: 0, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="name"
                    angle={-30}
                    textAnchor="end"
                    height={80}
                    tick={{ fontSize: 11 }}
                    interval={0}
                  />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} label={{ value: "min", angle: -90, position: "insideLeft", fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 11 }} label={{ value: "%", angle: 90, position: "insideRight", fontSize: 11 }} />
                  <Tooltip content={<CustomParetoTooltip />} />
                  <Bar
                    yAxisId="left"
                    dataKey="value"
                    name="Time Lost"
                    radius={[4, 4, 0, 0]}
                    maxBarSize={60}
                    onClick={(data) => onChartClick && onChartClick(data.name || data.payload?.name || data.name)}
                    style={{ cursor: "pointer" }}
                  >
                    {paretoWithCum.map((entry, index) => (
                      <Cell key={index} fill={entry.color} />
                    ))}
                  </Bar>
                  <Bar yAxisId="right" dataKey="cumPct" name="Cumulative %" fill="#529579" opacity={0.35} radius={[2, 2, 0, 0]} maxBarSize={60} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {activeChart === "dist" && (
            <div style={{ flex: "1", minWidth: "280px", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={distData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={3}
                    dataKey="value"
                    onClick={(data) => onChartClick && onChartClick(data.name || data.payload?.name || data.name)}
                    style={{ cursor: "pointer" }}
                  >
                    {distData.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              {/* Custom legend */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", justifyContent: "center" }}>
                {distData.map((d) => (
                  <div key={d.name} style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: d.fill }} />
                    <span style={{ fontSize: "11px", color: "#374151" }}>
                      {d.name} ({fmt(d.value)} min)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Loss Report Table ────────────────────────────────────────────────────────
const COL_W = [230, 160];
const COL_LEFT = [0, COL_W[0]];
const FREEZE_OPTIONS = [
  { label: "None", value: 0 },
  { label: "Machine", value: 1 },
  { label: "+ Loss", value: 2 },
];

const LossTable = ({ dates, machines, grand }) => {
  const [frozenCols, setFrozenCols] = useState(2);
  const [expandedRows, setExpandedRows] = useState({});

  const BG_HEADER = "#1e293b";
  const BG_GRAND = "#529579";

  const sticky = (colIdx, bg = "#fff") => {
    const isFrozen = frozenCols >= colIdx;
    const widthStyle = {
      minWidth: colIdx === 1 ? COL_W[0] : (colIdx === 2 ? COL_W[1] : undefined),
      width: colIdx === 1 ? COL_W[0] : (colIdx === 2 ? COL_W[1] : undefined),
      maxWidth: colIdx === 1 ? COL_W[0] : (colIdx === 2 ? COL_W[1] : undefined),
      boxSizing: 'border-box'
    };
    return isFrozen
      ? { ...widthStyle, position: "sticky", left: COL_LEFT[colIdx - 1], zIndex: 6 - colIdx, background: bg }
      : widthStyle;
  };

  const thStyle = (colIdx) => ({
    position: "sticky",
    top: 0,
    ...(frozenCols >= colIdx ? { left: COL_LEFT[colIdx - 1] } : {}),
    zIndex: frozenCols >= colIdx ? 22 - colIdx : 11,
    background: BG_HEADER,
    color: "#fff",
    minWidth: colIdx === 1 ? COL_W[0] : (colIdx === 2 ? COL_W[1] : undefined),
    width: colIdx === 1 ? COL_W[0] : (colIdx === 2 ? COL_W[1] : undefined),
    maxWidth: colIdx === 1 ? COL_W[0] : (colIdx === 2 ? COL_W[1] : undefined),
    boxSizing: 'border-box'
  });

  const shiftThStyle = () => ({
    position: "sticky", top: 0, zIndex: 10,
    background: BG_HEADER, color: "#fff",
  });

  const LOSS_KEYS = [
    { key: "startupLoss", label: "Startup Loss", color: LOSS_COLORS["Startup Loss"] },
    { key: "breakdownLoss", label: "Breakdown Loss", color: LOSS_COLORS["Breakdown Loss"] },
    { key: "changeoverLoss", label: "Changeover Loss", color: LOSS_COLORS["Changeover Loss"] },
    { key: "performanceLoss", label: "Performance Loss", color: LOSS_COLORS["Performance Loss"] },
    { key: "qualityLoss", label: "Quality Loss", color: LOSS_COLORS["Quality Loss"] },
  ];

  const toggleExpand = (key) =>
    setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));

  const cellStyle = (color, bold) => ({
    textAlign: "right",
    padding: "5px 10px",
    fontSize: "12px",
    fontWeight: bold ? 700 : 400,
    color: color || "#374151",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
  });

  return (
    <div className="lrt-outer">
      {/* Toolbar */}
      <div className="lrt-toolbar">
        <span className="lrt-toolbar-label">📌 Freeze columns:</span>
        <div className="lrt-freeze-btns">
          {FREEZE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`lrt-freeze-btn${frozenCols === opt.value ? " active" : ""}`}
              onClick={() => setFrozenCols(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <span style={{ marginLeft: "auto", fontSize: "11px", color: "#9ca3af" }}>
          Loss time in minutes
        </span>
      </div>

      <div className="lrt-wrapper">
        <table className="lrt-table">
          <thead>
            <tr>
              <th className="lrt-th lrt-th-machine" style={thStyle(1)}>Machine</th>
              <th className="lrt-th lrt-th-loss" style={thStyle(2)}>Loss Type</th>
              {dates.map((d, i) => (
                <th key={i} className="lrt-th lrt-ot" style={shiftThStyle()} title={fmtDate(d)}>
                  {d.getDate()}
                </th>
              ))}
              <th className="lrt-th lrt-ot" style={{ ...shiftThStyle(), fontWeight: 700 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {machines.map((machine, mIdx) => {
              const machRowKey = `mach-${mIdx}`;
              const isExpanded = expandedRows[machRowKey] !== false; // default expanded
              const totalRows = isExpanded ? LOSS_KEYS.length + 2 : 2; // +2 = total row + machine total

              return (
                <React.Fragment key={mIdx}>
                  {/* Machine row (rowspan over loss rows) rendered via separate display */}
                  {LOSS_KEYS.map((lt, ltIdx) => {
                    const isFirst = ltIdx === 0;
                    const machineRowspan = LOSS_KEYS.length + 1; // loss rows + machine total

                    return (
                      <tr
                        key={`${mIdx}-${ltIdx}`}
                        style={{ background: ltIdx % 2 === 0 ? "#fff" : "#f8fafc" }}
                      >
                        {isFirst && (
                          <td
                            className="lrt-cell lrt-machine"
                            rowSpan={machineRowspan}
                            style={{
                              ...sticky(1, "#f0f4ff"),
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "#1e40af",
                              verticalAlign: "top",
                              padding: "8px 10px",
                              borderRight: "1px solid #e5e7eb",
                              borderBottom: "2px solid #c7d2fe",
                            }}
                          >
                            {machine.label}
                          </td>
                        )}
                        <td
                          className="lrt-cell lrt-losstype"
                          style={{
                            ...sticky(2, ltIdx % 2 === 0 ? "#fff" : "#f8fafc"),
                            padding: "5px 10px",
                            fontSize: "12px",
                            fontWeight: 500,
                            color: lt.color,
                            borderBottom: "1px solid #e5e7eb",
                            borderRight: "1px solid #e5e7eb",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              width: "8px",
                              height: "8px",
                              background: lt.color,
                              borderRadius: "50%",
                              marginRight: "6px",
                            }}
                          />
                          {lt.label}
                        </td>
                        {machine.daily.map((day, di) => (
                          <td key={di} style={cellStyle(day ? lt.color : "#d1d5db", false)}>
                            {day ? fmt(day[lt.key]) : "–"}
                          </td>
                        ))}
                        <td style={cellStyle(lt.color, true)}>
                          {fmt(machine.totals[lt.key])}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Machine Total Row */}
                  <tr style={{ background: "#e8f0fe" }}>
                    <td
                      colSpan={1}
                      className="lrt-cell lrt-machine-total-label"
                      style={{
                        ...sticky(2, "#e8f0fe"),
                        padding: "6px 10px",
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#1d4ed8",
                        borderBottom: "2px solid #93c5fd",
                        borderTop: "1px solid #93c5fd",
                        borderRight: "1px solid #93c5fd",
                      }}
                    >
                      Machine Total
                    </td>
                    {machine.daily.map((day, di) => (
                      <td
                        key={di}
                        style={{
                          textAlign: "right",
                          padding: "6px 10px",
                          fontSize: "12px",
                          fontWeight: 700,
                          color: "#1d4ed8",
                          borderBottom: "2px solid #93c5fd",
                          borderTop: "1px solid #93c5fd",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {day ? fmt(day.totalLoss) : "–"}
                      </td>
                    ))}
                    <td
                      style={{
                        textAlign: "right",
                        padding: "6px 10px",
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#1d4ed8",
                        borderBottom: "2px solid #93c5fd",
                        borderTop: "1px solid #93c5fd",
                      }}
                    >
                      {fmt(machine.totals.totalLoss)}
                    </td>
                  </tr>

                  {/* Spacer */}
                  <tr className="lrt-row-spacer">
                    <td colSpan={2 + dates.length + 1} style={{ height: "6px" }} />
                  </tr>
                </React.Fragment>
              );
            })}

            {/* Grand Total */}
            {grand && (
              <tr style={{ background: BG_GRAND, color: "#fff" }}>
                <td
                  className="lrt-cell lrt-grand-label"
                  style={{
                    ...sticky(1, BG_GRAND),
                    padding: "8px 12px",
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#fff",
                    borderTop: "2px solid #4f46e5",
                  }}
                >
                  All Machine Total
                </td>
                <td
                  className="lrt-cell"
                  style={{
                    ...sticky(2, BG_GRAND),
                    padding: "8px 12px",
                    borderTop: "2px solid #4f46e5",
                  }}
                >

                </td>
                {/* Daily grand totals */}
                {dates.map((_, di) => {
                  const dayTotal = machines.reduce((sum, m) => {
                    const day = m.daily[di];
                    return sum + (day ? day.totalLoss : 0);
                  }, 0);
                  return (
                    <td
                      key={di}
                      style={{
                        textAlign: "right",
                        padding: "8px 10px",
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#fff",
                        borderTop: "2px solid #4f46e5",
                      }}
                    >
                      {fmt(dayTotal)}
                    </td>
                  );
                })}
                <td
                  style={{
                    textAlign: "right",
                    padding: "8px 10px",
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#fff",
                    borderTop: "2px solid #4f46e5",
                  }}
                >
                  {fmt(grand.totalLoss)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Page Component ───────────────────────────────────────────────────────────
const OeeLossReport = () => {
  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState(null);
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [viewType, setViewType] = useState("chart");

  const [selectedPlants, setSelectedPlants] = useState([]);
  const [selectedLines, setSelectedLines] = useState([]);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [selectedShifts, setSelectedShifts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedSkus, setSelectedSkus] = useState([]);

  const activeFilterCount =
    selectedPlants.length + selectedLines.length + selectedMachines.length +
    selectedShifts.length + selectedProducts.length + selectedSkus.length +
    (startDate ? 1 : 0) + (endDate ? 1 : 0);

  // ── Fetch data ──────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      setError("Please select both Start Date and End Date.");
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      setError("Start Date must be before End Date.");
      return;
    }
    setError("");
    showReportGeneratingToast();
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = {
        startDate,
        endDate,
        ...(selectedPlants.length > 0 && { plantId: selectedPlants[0] }),
        ...(selectedLines.length > 0 && { lineId: selectedLines[0] }),
        ...(selectedMachines.length > 0 && { machineId: selectedMachines[0] }),
        ...(selectedShifts.length > 0 && { shift: selectedShifts[0] }),
        ...(selectedProducts.length > 0 && { productId: selectedProducts[0] }),
        ...(selectedSkus.length > 0 && { skuId: selectedSkus[0] }),
      };
      const res = await apiClient.get("/reports/oee-financial-loss", { params, headers });
      setRawData(res.data);
      reportGenerationDone();
    } catch (err) {
      console.error(err);
      const msg = "Failed to fetch report data. Please check your connection and try again.";
      setError(msg);
      reportGenerationFailed(msg);
    } finally {
      setLoading(false);
    }
  };

  const clearAllFilters = () => {
    setSelectedPlants([]); setSelectedLines([]);
    setSelectedMachines([]); setSelectedShifts([]);
    setSelectedProducts([]); setSelectedSkus([]);
    setStartDate(""); setEndDate("");
    setRawData(null); setError("");
  };

  // ── Derived data (memoized) ─────────────────────────────────────────────────
  const dates = useMemo(() => buildDateRange(startDate, endDate), [startDate, endDate]);

  const lossSummary = useMemo(() => {
    if (!rawData || !Array.isArray(rawData) || rawData.length === 0) return null;
    return buildLossSummary(rawData, dates);
  }, [rawData, dates]);

  const isEmpty = rawData && Array.isArray(rawData) && rawData.length === 0;

  // ── Export ──────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    if (!lossSummary) return;
    const { machines, grand } = lossSummary;

    const wb = new ExcelJS.Workbook();
    wb.creator = "MaX";
    const ws = wb.addWorksheet("OEE Loss Report");

    const PURPLE = { type: "pattern", pattern: "solid", fgColor: { argb: "FF667EEA" } };
    const BLUE = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8F0FE" } };
    const WHITE = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFFFF" } };
    const LGRAY = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF8FAFC" } };
    const border = {
      top: { style: "thin", color: { argb: "FFE2E8F0" } },
      left: { style: "thin", color: { argb: "FFE2E8F0" } },
      bottom: { style: "thin", color: { argb: "FFE2E8F0" } },
      right: { style: "thin", color: { argb: "FFE2E8F0" } },
    };
    const ctr = { horizontal: "center", vertical: "middle" };
    const lft = { horizontal: "left", vertical: "middle" };

    const LOSS_KEYS = [
      { key: "startupLoss", label: "Startup Loss" },
      { key: "breakdownLoss", label: "Breakdown Loss" },
      { key: "changeoverLoss", label: "Changeover Loss" },
      { key: "performanceLoss", label: "Performance Loss" },
      { key: "qualityLoss", label: "Quality Loss" },
    ];

    ws.columns = [
      { width: 30 },  // Machine
      { width: 18 },  // Loss Type
      ...dates.map(() => ({ width: 8 })),   // Date cols
      { width: 9 },   // Total
    ];

    // Header row
    const hdr = ws.addRow(["Machine", "Loss Type", ...dates.map((d) => d.getDate()), "Total"]);
    hdr.height = 22;
    hdr.eachCell((c) => {
      c.fill = PURPLE;
      c.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 9 };
      c.border = border;
      c.alignment = ctr;
    });
    hdr.getCell(1).alignment = lft;
    hdr.getCell(2).alignment = lft;

    machines.forEach((machine) => {
      const machStart = ws.rowCount + 1;

      LOSS_KEYS.forEach((lt, ltIdx) => {
        const row = ws.addRow([
          ltIdx === 0 ? machine.label : "",
          lt.label,
          ...machine.daily.map((d) => (d ? Number(d[lt.key]) || 0 : "")),
          Number(machine.totals[lt.key]) || 0,
        ]);
        row.height = 15;
        row.eachCell((c) => {
          c.border = border;
          c.font = { size: 9 };
          c.alignment = ctr;
          c.fill = ltIdx % 2 === 0 ? WHITE : LGRAY;
        });
        row.getCell(1).alignment = lft;
        row.getCell(2).alignment = lft;
      });

      // Machine total row
      const mTotalRow = ws.addRow([
        "", "Machine Total",
        ...machine.daily.map((d) => (d ? Number(d.totalLoss) || 0 : "")),
        Number(machine.totals.totalLoss) || 0,
      ]);
      mTotalRow.height = 16;
      mTotalRow.eachCell((c) => {
        c.fill = BLUE;
        c.border = border;
        c.font = { bold: true, color: { argb: "FF1D4ED8" }, size: 9 };
        c.alignment = ctr;
      });
      mTotalRow.getCell(2).alignment = lft;
      ws.mergeCells(mTotalRow.number, 2, mTotalRow.number, 2);

      const machEnd = ws.rowCount;
      if (machEnd > machStart) {
        ws.mergeCells(machStart, 1, machEnd, 1);
        ws.getCell(machStart, 1).alignment = { vertical: "top", wrapText: true };
        ws.getCell(machStart, 1).font = { bold: true, size: 9, color: { argb: "FF1E40AF" } };
      }

      ws.addRow([]); // spacer
    });

    // Grand total
    const grandRow = ws.addRow([
      "All Machine Total", "",
      ...dates.map((_, i) => {
        const dayTotal = machines.reduce((sum, m) => sum + (m.daily[i] ? m.daily[i].totalLoss : 0), 0);
        return Math.round(dayTotal * 10) / 10;
      }),
      Number(grand.totalLoss) || 0,
    ]);
    grandRow.height = 18;
    ws.mergeCells(grandRow.number, 1, grandRow.number, 2);
    grandRow.eachCell((c) => {
      c.fill = PURPLE;
      c.border = border;
      c.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 9 };
      c.alignment = ctr;
    });
    grandRow.getCell(1).alignment = lft;

    ws.views = [{ state: "frozen", xSplit: 2, ySplit: 1, topLeftCell: "C2" }];

    const fromStr = new Date(startDate).toLocaleDateString("en-GB").replace(/\//g, "-");
    const toStr = new Date(endDate).toLocaleDateString("en-GB").replace(/\//g, "-");
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `OEE_Loss_Report_${fromStr}_to_${toStr}.xlsx`);
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  const hasData = lossSummary && lossSummary.machines.length > 0;

  // ── 3-Level Drilldown Modal Logic ──────────────────────────────────────────
  // Level 1: OEE Impact clicked (Availability/Performance/Quality) → show Loss Types
  // Level 2: Loss Type clicked → show Loss Sub Types  
  // Level 3: Loss Sub Type clicked → show Reasons
  const [drilldownState, setDrilldownState] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [allTrendRecords, setAllTrendRecords] = useState([]);

  const handleChartClick = async (impactName) => {
    setDrilldownLoading(true);
    setDrilldownState({ level: 1, impact: impactName, title: `${impactName} Loss — Loss Types`, data: [] });
    try {
      const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = {
        startDate, endDate,
        ...(selectedPlants.length > 0 && { plantId: selectedPlants[0] }),
        ...(selectedLines.length > 0 && { lineId: selectedLines[0] }),
        ...(selectedMachines.length > 0 && { machineId: selectedMachines[0] }),
        ...(selectedShifts.length > 0 && { shift: selectedShifts[0] }),
        ...(selectedProducts.length > 0 && { productId: selectedProducts[0] }),
        ...(selectedSkus.length > 0 && { skuId: selectedSkus[0] }),
      };
      const res = await apiClient.get("/reports/oee-loss-trends", { params, headers });
      const records = res.data;
      setAllTrendRecords(records);

      // Filter records belonging to the clicked OEE Impact category
      const relevant = records.filter(r => {
        const impact = (r.oeeImpact || "").toLowerCase();
        const t = (r.lossTypeName || "").toLowerCase();
        const impLow = impactName.toLowerCase();
        if (impLow === "availability") {
          // availability covers any that have impact=availability OR name-based fallback
          return impact === "availability" ||
            (impact === "" && (t.includes("startup") || t.includes("breakdown") || t.includes("change") || t.includes("setup") || t.includes("adjustment")));
        }
        if (impLow === "performance") {
          return impact === "performance" || (impact === "" && (t.includes("motion") || t.includes("speed") || t.includes("cycle")));
        }
        if (impLow === "quality") {
          return impact === "quality" || (impact === "" && t.includes("quality"));
        }
        return false;
      });

      // GROUP by Loss Type Name → Level 1 chart
      const lossTypeMap = {};
      relevant.forEach(r => {
        const lt = r.lossTypeName || "Unknown Loss Type";
        lossTypeMap[lt] = (lossTypeMap[lt] || 0) + r.durationMinutes;
      });
      const chartData = Object.keys(lossTypeMap)
        .map(k => ({ name: k, value: Math.round(lossTypeMap[k] * 10) / 10 }))
        .filter(d => d.value > 0)
        .sort((a, b) => b.value - a.value);

      setDrilldownState({
        level: 1, impact: impactName,
        title: `${IMPACT_ICONS[impactName] || ""} ${impactName} — Loss Types`,
        data: chartData, relevantRecords: relevant,
      });
    } catch (err) {
      console.error(err);
      setDrilldownState({ level: 1, impact: impactName, title: `Failed to load data`, data: [], relevantRecords: [] });
    } finally {
      setDrilldownLoading(false);
    }
  };

  // Level 1 → Level 2: clicked a Loss Type
  const handleLossTypeClick = (lossTypeName) => {
    if (!drilldownState?.relevantRecords) return;
    const filtered = drilldownState.relevantRecords.filter(r => (r.lossTypeName || "Unknown Loss Type") === lossTypeName);
    const subTypeMap = {};
    filtered.forEach(r => {
      const st = r.lossSubTypeName || "(No Sub Type)";
      subTypeMap[st] = (subTypeMap[st] || 0) + r.durationMinutes;
    });
    const chartData = Object.keys(subTypeMap)
      .map(k => ({ name: k, value: Math.round(subTypeMap[k] * 10) / 10 }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value);

    setDrilldownState(prev => ({
      ...prev, level: 2,
      title: `${prev.impact} › ${lossTypeName} — Sub Types`,
      selectedLossType: lossTypeName,
      data: chartData, filteredForSubType: filtered,
    }));
  };

  // Level 2 → Level 3: clicked a Sub Type
  const handleSubTypeClick = (subTypeName) => {
    if (!drilldownState?.filteredForSubType) return;
    const filtered = drilldownState.filteredForSubType.filter(r => (r.lossSubTypeName || "(No Sub Type)") === subTypeName);
    const reasonMap = {};
    filtered.forEach(r => {
      const reason = r.lossReasonName || "(No Reason)";
      reasonMap[reason] = (reasonMap[reason] || 0) + r.durationMinutes;
    });
    const chartData = Object.keys(reasonMap)
      .map(k => ({ name: k, value: Math.round(reasonMap[k] * 10) / 10 }))
      .filter(d => d.value > 0)
      .sort((a, b) => b.value - a.value);

    setDrilldownState(prev => ({
      ...prev, level: 3,
      title: `${prev.impact} › ${prev.selectedLossType} › ${subTypeName} — Reasons`,
      selectedSubType: subTypeName,
      data: chartData,
    }));
  };

  const closeDrilldown = () => setDrilldownState(null);
  const drilldownGoBack = () => {
    if (!drilldownState) return;
    if (drilldownState.level === 3) {
      // Back to Level 2: re-build sub type chart from filteredForSubType
      const subTypeMap = {};
      drilldownState.filteredForSubType.forEach(r => {
        const st = r.lossSubTypeName || "(No Sub Type)";
        subTypeMap[st] = (subTypeMap[st] || 0) + r.durationMinutes;
      });
      const chartData = Object.keys(subTypeMap)
        .map(k => ({ name: k, value: Math.round(subTypeMap[k] * 10) / 10 }))
        .filter(d => d.value > 0).sort((a, b) => b.value - a.value);
      setDrilldownState(prev => ({
        ...prev, level: 2,
        title: `${prev.impact} › ${prev.selectedLossType} — Sub Types`,
        selectedSubType: null, data: chartData,
      }));
    } else if (drilldownState.level === 2) {
      // Back to Level 1: re-build loss type chart from relevantRecords
      const lossTypeMap = {};
      drilldownState.relevantRecords.forEach(r => {
        const lt = r.lossTypeName || "Unknown Loss Type";
        lossTypeMap[lt] = (lossTypeMap[lt] || 0) + r.durationMinutes;
      });
      const chartData = Object.keys(lossTypeMap)
        .map(k => ({ name: k, value: Math.round(lossTypeMap[k] * 10) / 10 }))
        .filter(d => d.value > 0).sort((a, b) => b.value - a.value);
      setDrilldownState(prev => ({
        ...prev, level: 1,
        title: `${IMPACT_ICONS[prev.impact] || ""} ${prev.impact} — Loss Types`,
        selectedLossType: null, filteredForSubType: null, data: chartData,
      }));
    }
  };

  return (
    <div className="pagecontainer report-page">
      {/* Header */}
      <div className="page-header">
        <div className="oee-report-header-left">
          <button
            className="oee-report-back-btn"
            onClick={() => navigate("/home/reports")}
            title="Back to OEE Reports"
          >
            <HiOutlineArrowLeft size={18} />
          </button>
          <div>
            <h1 className="oee-report-title">
              <HiOutlineClipboardDocumentList /> Loss Report
            </h1>
            <p className="oee-report-subtitle">
              Detailed breakdown of losses and downtime reasons
            </p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="oee-filter-toggle-btn"
            onClick={() => setShowFilters((p) => !p)}
          >
            <HiOutlineAdjustmentsHorizontal size={16} />
            Filters
            {activeFilterCount > 0 && (
              <span className="oee-filter-badge">{activeFilterCount}</span>
            )}
            {showFilters ? <HiOutlineChevronUp size={13} /> : <HiOutlineChevronDown size={13} />}
          </button>
          <button
            className="btn-primary btn"
            onClick={handleExport}
            disabled={!hasData}
            title={hasData ? "Export to Excel" : "Generate a report first"}
          >
            <HiOutlineArrowDownTray /> Export
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="reportbox searchfilterbox">
          <div className="report-filters">
            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                className={`filter-input${error ? " input-error" : ""}`}
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setError(""); }}
              />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                className={`filter-input${error ? " input-error" : ""}`}
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setError(""); }}
              />
            </div>
            <ReportFilterBar
              selectedPlants={selectedPlants} onPlantChange={setSelectedPlants}
              selectedLines={selectedLines} onLineChange={setSelectedLines}
              selectedMachines={selectedMachines} onMachineChange={setSelectedMachines}
              selectedShifts={selectedShifts} onShiftChange={setSelectedShifts}
              selectedProducts={selectedProducts} onProductChange={setSelectedProducts}
              selectedSkus={selectedSkus} onSkuChange={setSelectedSkus}
            />
          </div>

          <div className="report-filter-actions">
            {activeFilterCount > 0 && (
              <button className="oee-clear-filters-btn" onClick={clearAllFilters}>
                ✕ Clear Filters
              </button>
            )}
            <button type="button" className="btn-primary" onClick={handleGenerate} disabled={loading}>
              <HiOutlineCalendar /> Generate Report
            </button>
          </div>
          {error && <p className="report-filter-error">{error}</p>}
        </div>
      )}

      {/* Report Content */}
      {rawData ? (
        <>
          {isEmpty ? (
            <div className="report-container">
              <div className="report-content">
                <div className="report-placeholder">
                  <HiOutlineExclamationCircle className="placeholder-icon" style={{ color: "#f59e0b" }} />
                  <h3>No Data Found</h3>
                  <p>No OEE entries found for the selected filters. Try a different date range or machine.</p>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Context notice */}
              <div style={{
                padding: "8px 24px",
                fontSize: "12px",
                color: "#1e40af",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}>
                <HiOutlineInformationCircle size={14} />
                Loss values are populated precisely from specific OEE downtime and cycle time log entries.
              </div>

              {/* Loss Summary Cards */}
              <LossSummaryCards grand={lossSummary?.grand} />

              {/* View Toggle */}
              <div style={{ padding: "12px 24px", borderRadius: "10px 10px 0px 0px", background: "#fff", borderBottom: "1px solid #e5e7eb", display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                <span style={{ fontSize: "13px", fontWeight: 600, color: "#374151", alignSelf: "center", marginRight: "10px" }}>View Type:</span>
                <button
                  onClick={() => setViewType("chart")}
                  style={{
                    padding: "6px 14px", borderRadius: "6px", border: "1px solid #e5e7eb; border-color: var(--border-primary)",
                    background: viewType === "chart" ? "#529679" : "#fff",
                    color: viewType === "chart" ? "#fff" : "#374151",
                    fontSize: "12px", fontWeight: 500, cursor: "pointer", transition: "all 0.2s"
                  }}
                >
                  Chart View
                </button>
                <button
                  onClick={() => setViewType("table")}
                  style={{
                    padding: "6px 14px", borderRadius: "6px", border: "1px solid #e5e7eb; border-color: var(--border-primary)",
                    background: viewType === "table" ? "#529579" : "#fff",
                    color: viewType === "table" ? "#fff" : "#374151",
                    fontSize: "12px", fontWeight: 500, cursor: "pointer", transition: "all 0.2s"
                  }}
                >
                  Table View
                </button>
              </div>

              {viewType === "chart" ? (
                <LossCharts grand={lossSummary.grand} machines={lossSummary.machines} onChartClick={handleChartClick} />
              ) : (
                <LossTable
                  dates={dates}
                  machines={lossSummary?.machines || []}
                  grand={lossSummary?.grand}
                />
              )}
            </>
          )}
        </>
      ) : (
        <div className="report-container">
          <div className="report-content">
            <div className="report-placeholder">
              <HiOutlineClipboardDocumentList className="placeholder-icon" />
              <h3>Loss Report</h3>
              <p>Select a date range and click "Generate Report" to view dynamic loss data</p>
            </div>
          </div>
        </div>
      )}

      {/* 3-Level Drilldown Modal */}
      <Dialog open={!!drilldownState} onClose={closeDrilldown} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}>
        <DialogTitle className="oee-drilldown-dialog-title">
          <div className="oee-drilldown-title-inner">
            {drilldownState && drilldownState.level > 1 && (
              <IconButton onClick={drilldownGoBack} size="small"
                className="oee-drilldown-back-btn">
                <HiOutlineArrowLeft size={16} />
              </IconButton>
            )}
            <div>
              {/* Level breadcrumb pills */}
              <div className="oee-drilldown-breadcrumbs">
                {['Impact', 'Loss Type', 'Sub Type', 'Reason'].slice(0, drilldownState?.level + 1).map((step, i) => (
                  <span
                    key={step}
                    className={i < (drilldownState?.level || 0) ? 'oee-drilldown-pill oee-drilldown-pill--done' : 'oee-drilldown-pill oee-drilldown-pill--active'}
                  >
                    Step {i + 1}: {step}
                  </span>
                ))}
              </div>
              <span className="oee-drilldown-heading">
                {drilldownState?.title}
              </span>
            </div>
          </div>
          <IconButton onClick={closeDrilldown} size="small" className="oee-drilldown-close">
            <HiOutlineXMark size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent className="oee-drilldown-dialog-content">
          {drilldownLoading ? (
            <div className="oee-drilldown-loading">
              <CircularProgress size={40} sx={{ color: 'var(--accent-primary)' }} />
            </div>
          ) : drilldownState && drilldownState.data.length > 0 ? (
            <>
              {/* Instruction hint */}
              <div className="oee-drilldown-hint">
                <span style={{ fontSize: '1rem' }}>
                  {drilldownState.level === 1 ? '🔵' : drilldownState.level === 2 ? '🟣' : '🟡'}
                </span>
                <span>
                  {drilldownState.level === 1 && <><strong>Loss Types</strong> for this impact category. Click a bar to see Sub Types.</>}
                  {drilldownState.level === 2 && <><strong>Sub Types</strong> for the selected loss type. Click a bar to see Reasons.</>}
                  {drilldownState.level === 3 && <><strong>Reasons</strong> logged for this sub type.</>}
                </span>
                <span className="oee-drilldown-hint-count">{drilldownState.data.length} item{drilldownState.data.length !== 1 ? 's' : ''}</span>
              </div>

              <ResponsiveContainer width="100%" height={Math.max(280, drilldownState.data.length * 48 + 40)}>
                <BarChart data={drilldownState.data} layout="vertical"
                  margin={{ top: 4, right: 50, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="var(--border-primary)" />
                  <XAxis type="number" tick={{ fontSize: 12, fill: 'var(--text-secondary)' }}
                    axisLine={{ stroke: 'var(--border-primary)' }} tickLine={false}
                    label={{ value: 'Minutes', position: 'insideBottom', offset: -4, fontSize: 11, fill: 'var(--text-tertiary)' }} />
                  <YAxis dataKey="name" type="category"
                    width={180} tick={{ fontSize: 12, fill: 'var(--text-primary)', fontWeight: 500 }}
                    axisLine={{ stroke: 'var(--border-primary)' }} tickLine={false} />
                  <Tooltip
                    cursor={{ fill: 'var(--bg-secondary)' }}
                    contentStyle={{ borderRadius: '10px', border: '1px solid var(--border-primary)', boxShadow: 'var(--shadow-md)', fontSize: '13px', background: 'var(--bg-card)' }}
                    formatter={(value) => [`${value} min`, 'Duration']}
                  />
                  <Bar
                    dataKey="value"
                    radius={[0, 6, 6, 0]}
                    maxBarSize={40}
                    onClick={(data) => {
                      const name = data.name || data.payload?.name;
                      if (drilldownState.level === 1) handleLossTypeClick(name);
                      else if (drilldownState.level === 2) handleSubTypeClick(name);
                    }}
                    style={{ cursor: drilldownState.level < 3 ? 'pointer' : 'default' }}
                  >
                    {drilldownState.data.map((entry, index) => {
                      const colors = ['#3b82f6', '#8b5cf6', '#f59e0b', SEMANTIC_SUCCESS_MID, '#ef4444', '#06b6d4'];
                      const levelColors = {
                        1: [
                          '#ef4444', '#f97316', '#f59e0b', '#84cc16', SEMANTIC_SUCCESS_MID, '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6', '#ec4899'
                        ],
                        2: [
                          '#6366f1', '#8b5cf6', '#a78bfa', '#c084fc', '#e879f9', '#f472b6', '#fb7185', '#fda4af'
                        ],
                        3: [
                          '#f59e0b', '#fbbf24', '#fcd34d', '#fde68a', '#fef3c7'
                        ]
                      };
                      const palette = levelColors[drilldownState.level] || colors;
                      return <Cell key={`cell-${index}`} fill={palette[index % palette.length]} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </>
          ) : (
            <div className="oee-drilldown-empty">
              <span style={{ fontSize: '2.5rem', marginBottom: '12px' }}>📭</span>
              <span style={{ fontSize: '0.95rem', fontWeight: 600 }}>No data found</span>
              <span className="oee-drilldown-empty-sub">No downtime logs for this category in the selected date range.</span>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OeeLossReport;
